import { FormulaValue, FunctionImpl } from '../types';
import { removeAccents } from 'common/utils/removeAccents';
import { registerMathFunctions } from './math';
import { registerLogicalFunctions } from './logical';
import { registerTextFunctions } from './text';
import { registerInformationFunctions } from './information';
import { registerStatisticalFunctions } from './statistical';
import { registerFinancialFunctions } from './financial';
import { registerDateFunctions } from './date';
import { registerEngineeringFunctions } from './engineering';
import { registerLookupFunctions } from './lookup';
import { registerMatrixFunctions } from './matrix';

export class FunctionRegistry {
    private readonly functions: Map<string, FunctionImpl> = new Map();
    private readonly aliases: Map<string, string> = new Map(); // uppercased localized name → english name

    register(englishName: string, impl: FunctionImpl): void {
        this.functions.set(englishName.toUpperCase(), impl);
    }

    registerLanguage(mapping: Record<string, string>): void {
        for (const [englishKey, localizedName] of Object.entries(mapping)) {
            if (localizedName && localizedName.trim() !== '') {
                const upper = localizedName.toUpperCase();
                this.aliases.set(upper, englishKey.toUpperCase());
                // Also register accent-stripped version for accent-insensitive matching
                const stripped = removeAccents(localizedName).toUpperCase();
                if (stripped !== upper) {
                    this.aliases.set(stripped, englishKey.toUpperCase());
                }
            }
        }
    }

    resolve(name: string): FunctionImpl | undefined {
        const upper = name.toUpperCase();
        // Try direct english name first
        const direct = this.functions.get(upper);
        if (direct) return direct;

        // Try alias (localized name → english name)
        const englishKey = this.aliases.get(upper);
        if (englishKey) {
            return this.functions.get(englishKey);
        }

        return undefined;
    }

    getAllFunctions(): Map<string, FunctionImpl> {
        return this.functions;
    }

    getAliases(): Map<string, string> {
        return this.aliases;
    }
}

// Helper: flatten args that may contain arrays into a flat number array
export function flattenArgs(args: FormulaValue[]): number[] {
    const result: number[] = [];
    for (const arg of args) {
        if (Array.isArray(arg)) {
            result.push(...arg);
        } else if (typeof arg === 'number') {
            result.push(arg);
        } else if (typeof arg === 'boolean') {
            result.push(arg ? 1 : 0);
        } else if (typeof arg === 'string') {
            const n = Number(arg);
            if (!isNaN(n)) result.push(n);
        } else if (arg === null || arg === undefined) {
            result.push(0);
        }
    }
    return result;
}

// Helper: convert a single value to number
export function toNum(val: FormulaValue): number {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    if (typeof val === 'boolean') return val ? 1 : 0;
    if (typeof val === 'string') {
        const n = Number(val.replace(',', '.'));
        return isNaN(n) ? 0 : n;
    }
    if (Array.isArray(val)) return val.length > 0 ? val[0] : 0;
    return 0;
}

// Helper: convert a value to string
export function toStr(val: FormulaValue): string {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
    if (Array.isArray(val)) return val.join(',');
    return String(val);
}

// Create and return a fully initialized function registry
export function createRegistry(): FunctionRegistry {
    const registry = new FunctionRegistry();

    registerMathFunctions(registry);
    registerLogicalFunctions(registry);
    registerTextFunctions(registry);
    registerInformationFunctions(registry);
    registerStatisticalFunctions(registry);
    registerFinancialFunctions(registry);
    registerDateFunctions(registry);
    registerEngineeringFunctions(registry);
    registerLookupFunctions(registry);
    registerMatrixFunctions(registry);

    return registry;
}
